import { getCalApi } from '@calcom/embed-react';
import {
  ChatBubbleOutline,
  Check as CheckIcon,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  ButtonBase,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useRef, useState, useEffect } from 'react';

import AdvisorChatTab from './AdvisorChatTab';
import AskHauserTab from './AskHauserTab';
import { ChatPillTab } from './ChatPillTab';
import { InboxList } from './InboxList';
import { PropertyStatsFooter } from './PropertyStatsFooter';

import { getInitials, UserAvatar } from '@/components/common/UserAvatar';
import { TasksIcon } from '@/components/dashboard/navigation/SidebarIcons';
import { ActivityPanel } from '@/components/dashboard/sidebar/ActivityPanel';
import { TasksPanel } from '@/components/dashboard/sidebar/TasksPanel';
import { CloseButton } from '@/components/ds';
import { StickyNotesCard } from '@/components/home/cards/StickyNotesCard';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useTasks } from '@/hooks/tasks';
import { useClientGroups } from '@/hooks/useClientGroups';
import { useUserRole } from '@/hooks/useUserRole';
import { TaskGroup } from '@/models/Task';
import { getPublicUserProfile, getUserPresence } from '@/services/api/users/api';
import { ClientGroup, Deal } from '@/types/clientGroups';

/* ─── Hauser "A" letterform (no circle bg) for side tab ──── */

const HauserA = ({ size = 24, color = '#191919' }: { size?: number; color?: string }) => (
  <svg width={size} height={size * 1.5} viewBox="0 0 424.56 635.11" style={{ flexShrink: 0 }}>
    <path
      d="M283.36,0h-142.15L0,635.11h424.56L283.36,0ZM146.34,520.05l62.64-313.19,2.83-22.68,2.83,22.68,63.33,313.19h-131.63Z"
      fill={color}
    />
  </svg>
);

/* ─── constants ──────────────────────────────────────────── */

export const PANEL_WIDTH = 380;
// the Ask Hauser / chat pull tabs on the panel's outer edge
const EDGE_TAB_WIDTH = 36;
// the frame gutter (DashboardLayout GUTTER): the panel's space above and below
const PANEL_GUTTER = 20;
const CHAT_PANEL_Z_INDEX = 1250;
const CHAT_EDGE_TABS_Z_INDEX = CHAT_PANEL_Z_INDEX + 1;
const CAL_NAMESPACE = 'advisor-chat';

const calLinkPathFrom = (calLink: string | undefined | null): string | null => {
  if (!calLink) return null;
  let trimmed = calLink.trim().replace(/^https?:\/\//i, '');
  const idx = trimmed.toLowerCase().indexOf('cal.com/');
  if (idx !== -1) trimmed = trimmed.slice(idx + 'cal.com/'.length);
  const path = trimmed.replace(/^\/+/, '').replace(/\/+$/, '');
  return path || null;
};

type ChatTab = 'askHauser' | 'advisor' | 'tasks';

/* ─── types ──────────────────────────────────────────────── */

interface ChatPanelProps {
  open: boolean;
  onOpen: (tab?: ChatTab) => void;
  onClose: () => void;
  propertyId?: string;
  ownerNames?: string;
}

/* ─── component ──────────────────────────────────────────── */

const ChatPanel = ({ open, onOpen, onClose, propertyId, ownerNames }: ChatPanelProps) => {
  const [activeTab, setActiveTab] = useState<ChatTab>('tasks');
  const [tasksSubTab, setTasksSubTab] = useState<'tasks' | 'activity'>('tasks');
  const [propertyPickerOpen, setPropertyPickerOpen] = useState(false);
  const [advisorViewMode, setAdvisorViewMode] = useState<'thread' | 'inbox'>('thread');
  const {
    cardMode,
    isAskHauserOpen,
    pendingGroupId,
    pendingDrawerTabRequest,
    pendingAskHauserQuestion,
    clearPendingGroupId,
    pendingInboxRequest,
    clearPendingInboxRequest,
  } = useDashboardLayout();
  const { selectedProperty } = usePropertiesContext();
  const { isAdvisor, isAdmin } = useUserRole();

  const { groups, isLoading: isLoadingGroups } = useClientGroups();
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const isOwnerMode = !isAdvisor && !isAdmin && (selectedProperty?.isOwned ?? false);
  const isIndependentOwner = isOwnerMode && !isLoadingGroups && groups.length === 0;
  const advisorChatEnabled = !isIndependentOwner;
  // `?? groups[0]` only applies when there's no property context at all (see
  // the "Initial fallback" effect below) — it must NOT fire when a property
  // IS selected but has no matching group, or the tab silently shows an
  // arbitrary, unrelated client instead of the "no client" state.
  const edgeConversationGroup = useMemo(() => {
    const selected = groups.find((g) => g.id === selectedGroupId);
    if (selected) return selected;
    if (propertyId) return groups.find((g) => g.propertyIds?.includes(propertyId));
    return groups[0];
  }, [groups, propertyId, selectedGroupId]);
  const edgeAdvisorUserId = !isAdvisor ? edgeConversationGroup?.advisorId : undefined;
  const { data: edgeAdvisorProfile } = useQuery({
    queryKey: ['public-profile', edgeAdvisorUserId],
    queryFn: () => getPublicUserProfile(edgeAdvisorUserId as string),
    enabled: Boolean(edgeAdvisorUserId),
    staleTime: 5 * 60 * 1000,
  });
  const edgeMember = edgeConversationGroup?.members[0];
  const edgeConversationName = isAdvisor
    ? edgeMember?.name || 'Client'
    : edgeAdvisorProfile?.name || edgeMember?.name || 'Advisor';
  const edgeConversationPhotoUrl = isAdvisor ? edgeMember?.photoUrl : edgeAdvisorProfile?.photoUrl;

  // Explicit navigation (e.g. clicking chat on the clients page) always wins.
  useEffect(() => {
    if (pendingGroupId) {
      if (!advisorChatEnabled) {
        clearPendingGroupId();
        return;
      }
      setSelectedGroupId(pendingGroupId);
      setActiveTab('advisor');
      setAdvisorViewMode('thread');
      clearPendingGroupId();
    }
  }, [pendingGroupId]);

  // "Messages" nav click — if the current property has a client group
  // attached, jump straight into that thread (that's almost always what you
  // want when you're already looking at a specific property). Only fall
  // back to the full inbox list when there's no property context or no
  // group is attached to it.
  useEffect(() => {
    if (!pendingInboxRequest) return;
    if (!advisorChatEnabled) {
      clearPendingInboxRequest();
      return;
    }
    const matched = propertyId ? groups.find((g) => g.propertyIds?.includes(propertyId)) : null;
    if (matched) {
      setSelectedGroupId(matched.id);
      setAdvisorViewMode('thread');
    } else {
      setAdvisorViewMode('inbox');
    }
    setActiveTab('advisor');
    clearPendingInboxRequest();
  }, [pendingInboxRequest, propertyId, groups]);

  useEffect(() => {
    if (isAskHauserOpen && pendingAskHauserQuestion) {
      setActiveTab('askHauser');
    }
  }, [isAskHauserOpen, pendingAskHauserQuestion]);

  useEffect(() => {
    if (pendingDrawerTabRequest) setActiveTab(pendingDrawerTabRequest.tab);
  }, [pendingDrawerTabRequest]);

  useEffect(() => {
    if (!advisorChatEnabled && activeTab === 'advisor') {
      setActiveTab('tasks');
      setAdvisorViewMode('thread');
    }
  }, [advisorChatEnabled, activeTab]);

  // Follow the selected property via the propertyId prop, but only when the property
  // itself changes — never override a manual group pick.
  const syncedPropertyIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (pendingGroupId) return;
    // A fresh "Messages" nav click already does its own property→group
    // matching (see the pendingInboxRequest effect above) — skip here so
    // this effect doesn't redundantly process the same mount.
    if (pendingInboxRequest) return;
    if (syncedPropertyIdRef.current === propertyId) return;
    syncedPropertyIdRef.current = propertyId;
    const matched = propertyId ? groups.find((g) => g.propertyIds?.includes(propertyId)) : null;
    if (matched) {
      setSelectedGroupId(matched.id);
      // If the inbox list is open when the property changes, jump to the
      // newly-matched thread rather than silently updating the selection
      // underneath a list the user is still looking at.
      setAdvisorViewMode('thread');
    } else if (propertyId) {
      // No client group is attached to this property (rare). Don't leave
      // whatever thread was previously selected on screen — that's a
      // different, unrelated client, and silently showing it risks someone
      // mistaking it for this property's conversation. Show the inbox
      // instead of any specific thread, and clear the stale selection so
      // the edge tab's avatar doesn't keep pointing at it either.
      setSelectedGroupId(null);
      setAdvisorViewMode('inbox');
    }
  }, [propertyId, groups, pendingGroupId, pendingInboxRequest]);

  // Initial fallback: only when there's no property context at all (e.g. no
  // property selected yet) — pick the first group so the panel isn't empty.
  // A page WITH a propertyId is always handled by the property-sync effect
  // above, matched or not; this must not fire for that case, or it clobbers
  // a correct match with an arbitrary group on the very same initial render.
  useEffect(() => {
    if (propertyId) return;
    if (selectedGroupId) return;
    const groupId = groups[0]?.id ?? null;
    if (groupId) setSelectedGroupId(groupId);
  }, [groups, selectedGroupId, propertyId]);

  // Resolve the deal whose tasks the Tasks panel should display by looking up
  // which deal listing references the currently-selected property. A property
  // can in theory live in multiple deals (e.g. an old Closed engagement and a
  // new active one) — prefer non-Closed, then most recent.
  const dealForProperty = useMemo<{ deal: Deal; group: ClientGroup } | undefined>(() => {
    if (!propertyId) return undefined;
    const candidates: { deal: Deal; group: ClientGroup }[] = [];
    for (const group of groups) {
      for (const deal of group.deals ?? []) {
        const hasProperty = (deal.listings ?? []).some((l) => l.propertyId === propertyId);
        if (hasProperty) candidates.push({ deal, group });
      }
    }
    if (candidates.length === 0) return undefined;
    candidates.sort((a, b) => {
      const aClosed = a.deal.status === 'Closed' ? 1 : 0;
      const bClosed = b.deal.status === 'Closed' ? 1 : 0;
      if (aClosed !== bClosed) return aClosed - bClosed;
      return b.deal.createdAt - a.deal.createdAt;
    });
    return candidates[0];
  }, [groups, propertyId]);

  // Admins are not engagement participants — disable Tasks for them entirely.
  // Advisors and clients fall back to the property-not-in-a-deal message.
  const tasksDisabled = isAdmin || !dealForProperty;
  const tasksDisabledMessage = isAdmin ? 'No deals for admin' : 'No deal for this property';

  const selectedDealId = tasksDisabled ? undefined : dealForProperty?.deal.id;

  // Scope tasks to the current property's listing so DueDiligenceReview and
  // OfferStrategy stay isolated per listing. Without listingId the BE returns
  // every task in the deal, which aggregates these task groups across listings.
  const selectedListingId = useMemo(() => {
    if (tasksDisabled || !dealForProperty || !propertyId) return undefined;
    return dealForProperty.deal.listings?.find((l) => l.propertyId === propertyId)?.id;
  }, [tasksDisabled, dealForProperty, propertyId]);

  // Watch task state to flip the header subtitle once Onboarding is done.
  // Only advisors need this — gate to avoid unnecessary fetches for buyers.
  const { tasks: dealTasks } = useTasks(isAdvisor ? selectedDealId : undefined);
  const onboardingComplete = useMemo(() => {
    if (!dealTasks || dealTasks.length === 0) return false;
    const onboardingTasks = dealTasks.filter((t) => t.group === TaskGroup.Onboarding);
    if (onboardingTasks.length === 0) return false;
    return onboardingTasks.every((t) => Boolean(t.completedAt));
  }, [dealTasks]);

  // Header: advisors get "Deal • Client" while in Onboarding, then flip to
  // "Deal • <street address>" once Onboarding is complete and a property is
  // associated. Clients usually have one deal so the header is skipped.
  const tasksHeader = useMemo(() => {
    if (!dealForProperty || !isAdvisor) return undefined;
    const dealName = dealForProperty.deal.name;
    const clientName = dealForProperty.group.holdingName || 'Client';
    const listing = dealForProperty.deal.listings?.find((l) => l.propertyId === propertyId);
    const propertyStreet = listing?.address?.split(',')[0]?.trim();
    const subtitle = onboardingComplete && propertyStreet ? propertyStreet : clientName;
    return (
      <Box sx={{ px: 2, pt: 1, pb: 1.5 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            color: 'text.secondary2',
          }}>
          Deal
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {dealName}
          </Typography>
          <Typography sx={{ fontSize: 12, color: 'text.secondary2', flexShrink: 0 }}>•</Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>
    );
  }, [dealForProperty, isAdvisor, propertyId, onboardingComplete]);

  // Reset property picker when the drawer closes or user switches away from advisor tab.
  useEffect(() => {
    if (!open) setPropertyPickerOpen(false);
  }, [open]);

  useEffect(() => {
    if (activeTab !== 'advisor') setPropertyPickerOpen(false);
  }, [activeTab]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const panelWidth = isMobile ? 'min(100vw, 350px)' : PANEL_WIDTH;
  // a phone's pull tabs are slimmer so they cover less of the cards' right edge (the score figures sit there)
  const tabWidth = isMobile ? 28 : EDGE_TAB_WIDTH;
  const tabHeight = isMobile ? 64 : 80;
  const panelRef = useRef<HTMLDivElement>(null);

  // Prevent touchmove on the panel from propagating to body (iOS Safari rubber-band fix).
  // Only allow touchmove if the touch target is inside a scrollable child.
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;
    const handler = (e: TouchEvent) => {
      let el = e.target as HTMLElement | null;
      while (el && el !== panel) {
        if (el.scrollHeight > el.clientHeight && getComputedStyle(el).overflowY !== 'hidden') {
          // Inside a scrollable area — allow it
          return;
        }
        el = el.parentElement;
      }
      // Not inside a scrollable child — block to prevent body rubber-band
      e.preventDefault();
    };
    panel.addEventListener('touchmove', handler, { passive: false });
    return () => panel.removeEventListener('touchmove', handler);
  }, []);

  const handleEdgeTabClick = (tab: ChatTab) => {
    if (open && activeTab === tab) {
      onClose();
    } else {
      if (tab === 'advisor') {
        if (edgeConversationGroup?.id) {
          setSelectedGroupId(edgeConversationGroup.id);
          setAdvisorViewMode('thread');
        } else {
          setAdvisorViewMode('inbox');
        }
      }
      setActiveTab(tab);
      if (!open) onOpen(tab);
    }
  };

  return (
    <>
      {/* ─── Dual edge tabs ──────────────────────────────────── */}
      <Box
        sx={{
          position: 'fixed',
          top: isMobile ? '80%' : '50%',
          right: isMobile ? (open ? 'min(100vw, 350px)' : 0) : open ? PANEL_WIDTH : 0,
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: '5px',
          zIndex: CHAT_EDGE_TABS_Z_INDEX,
          transition: 'right 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        }}>
        {/* Ask Hauser edge tab */}
        <Tooltip title={open ? '' : 'Ask Hauser'} placement="left">
          <ButtonBase
            data-testid="chat-panel-ask-hauser-tab"
            onClick={() => handleEdgeTabClick('askHauser')}
            sx={{
              width: tabWidth,
              height: tabHeight,
              backgroundColor: 'success2.main',
              borderRadius: '10px 0 0 10px',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <HauserA size={14} />
            {open && activeTab === 'askHauser' ? (
              <ChevronRight
                sx={{ fontSize: 14, color: 'background.paper', position: 'absolute', bottom: 5 }}
              />
            ) : (
              <ChevronLeft
                sx={{
                  fontSize: 14,
                  color: 'background.paper',
                  position: 'absolute',
                  bottom: 5,
                }}
              />
            )}
          </ButtonBase>
        </Tooltip>

        {/* Edge tab — advisor sees "Clients" (their client conversations);
            client (buyer/owner) sees "Advisor" (the conversation with
            their assigned advisor). Independent owners do not have an
            advisor relationship yet, so keep this dormant without removing
            the chat implementation. */}
        {advisorChatEnabled && (
          <Tooltip title={open ? '' : isAdvisor ? 'Clients' : 'Advisor'} placement="left">
            <ButtonBase
              data-testid="chat-panel-advisor-tab"
              onClick={() => handleEdgeTabClick('advisor')}
              sx={{
                width: tabWidth,
                height: tabHeight,
                backgroundColor: '#dfdfdf',
                border: 0,
                borderRadius: '10px 0 0 10px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Avatar
                src={edgeConversationGroup ? edgeConversationPhotoUrl ?? undefined : undefined}
                alt={edgeConversationGroup ? edgeConversationName : 'No client on this property'}
                sx={{
                  width: 22,
                  height: 22,
                  fontSize: 10,
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 600,
                  backgroundColor: 'background.paper',
                  color: 'var(--tx-text)',
                }}>
                {edgeConversationGroup ? (
                  getInitials(edgeConversationName)
                ) : (
                  <ChatBubbleOutline sx={{ fontSize: 13 }} />
                )}
              </Avatar>
              {open && activeTab === 'advisor' ? (
                <ChevronRight
                  sx={{ fontSize: 14, color: 'background.paper', position: 'absolute', bottom: 5 }}
                />
              ) : (
                <ChevronLeft
                  sx={{
                    fontSize: 14,
                    color: 'background.paper',
                    position: 'absolute',
                    bottom: 5,
                  }}
                />
              )}
            </ButtonBase>
          </Tooltip>
        )}

        {/* Tasks edge tab */}
        <Tooltip
          title={open ? '' : tasksDisabled ? tasksDisabledMessage : 'Tasks & Activity'}
          placement="left">
          <ButtonBase
            data-testid="chat-panel-tasks-tab"
            onClick={() => handleEdgeTabClick('tasks')}
            sx={{
              width: tabWidth,
              height: isMobile ? 36 : 40,
              backgroundColor: 'background.paper',
              borderRadius: '10px 0 0 10px',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <TasksIcon size={14} color="var(--tx-text)" />
          </ButtonBase>
        </Tooltip>
      </Box>

      {/* ─── Panel ────────────────────────────────────────── */}
      <Box
        id="dashboard-chat-panel"
        data-open={open ? 'true' : 'false'}
        ref={panelRef}
        sx={{
          position: 'fixed',
          ...(isMobile
            ? {
                top: 0,
                bottom: 0,
                right: open
                  ? 0
                  : `calc(-1 * ${typeof panelWidth === 'number' ? `${panelWidth}px` : panelWidth})`,
                boxShadow: 'none',
              }
            : {
                top: `${PANEL_GUTTER}px`,
                bottom: `${PANEL_GUTTER}px`,
                right: open
                  ? 0
                  : `calc(-1 * ${typeof panelWidth === 'number' ? `${panelWidth}px` : panelWidth})`,
                borderRadius: '25px 0 0 25px',
                boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
              }),
          backgroundColor: 'background.paper',
          width: panelWidth,
          display: 'flex',
          flexDirection: 'column',
          zIndex: CHAT_PANEL_Z_INDEX,
          overflow: 'hidden',
          transition: 'right 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          pointerEvents: open ? 'auto' : 'none',
        }}>
        {/* ─── Tab bar + Close ──────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: '20px',
            pt: '20px',
            pb: '20px',
            flexShrink: 0,
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              // the ds Segmented track exactly: 3px inset on the fill, the control radius (12)
              gap: '2px',
              backgroundColor: 'var(--tx-fill)',
              borderRadius: '12px',
              p: '3px',
            }}>
            {activeTab === 'tasks' ? (
              <>
                <ChatPillTab
                  data-testid="chat-panel-tasks-subtab"
                  label="Tasks"
                  isActive={tasksSubTab === 'tasks'}
                  onClick={() => setTasksSubTab('tasks')}
                />
                <ChatPillTab
                  data-testid="chat-panel-activity-subtab"
                  label="Activity"
                  isActive={tasksSubTab === 'activity'}
                  onClick={() => setTasksSubTab('activity')}
                />
              </>
            ) : (
              <>
                <ChatPillTab
                  data-testid="chat-panel-ask-hauser-pill"
                  label="Ask Hauser"
                  isActive={activeTab === 'askHauser'}
                  onClick={() => setActiveTab('askHauser')}
                />
                {advisorChatEnabled && (
                  <ChatPillTab
                    data-testid="chat-panel-advisor-pill"
                    label={isAdvisor ? 'Clients' : 'Advisor'}
                    isActive={activeTab === 'advisor'}
                    onClick={() => setActiveTab('advisor')}
                  />
                )}
              </>
            )}
          </Box>
          <span data-testid="chat-panel-close-btn" style={{ display: 'flex' }}>
            <CloseButton onClose={onClose} label="Close" />
          </span>
        </Box>

        {/* ─── Tab Content ──────────────────────────────────── */}
        {activeTab === 'askHauser' && (
          <AskHauserTab
            propertyId={propertyId}
            mode={isOwnerMode ? 'homeowner' : 'buyer'}
            ownerNames={ownerNames}
          />
        )}

        {activeTab === 'advisor' && advisorViewMode === 'inbox' && (
          <InboxList
            onSelectGroup={(id) => {
              setSelectedGroupId(id);
              setAdvisorViewMode('thread');
            }}
          />
        )}

        {activeTab === 'advisor' && advisorViewMode === 'thread' && (
          <>
            <Box
              data-testid="chat-panel-back-to-inbox"
              onClick={() => setAdvisorViewMode('inbox')}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                px: '20px',
                pb: '10px',
                cursor: 'pointer',
                width: 'fit-content',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}>
              <ChevronLeft sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Inbox</Typography>
            </Box>
            <AdvisorHeader
              selectedGroupId={selectedGroupId}
              onSelectGroup={setSelectedGroupId}
              onOpenPropertyPicker={() => setPropertyPickerOpen(true)}
            />
            <AdvisorChatTab
              onClose={onClose}
              selectedGroupId={selectedGroupId}
              propertyPickerOpen={propertyPickerOpen}
              onClosePropertyPicker={() => setPropertyPickerOpen(false)}
            />
          </>
        )}

        {activeTab === 'tasks' && (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flex: 1, overflow: 'auto', px: '20px', pb: '20px' }}>
              {tasksSubTab === 'tasks' &&
                (isOwnerMode ? (
                  <StickyNotesCard />
                ) : (
                  <TasksPanel
                    cardMode={cardMode}
                    dealId={selectedDealId}
                    listingId={selectedListingId}
                    header={tasksHeader}
                    emptyMessage={tasksDisabled ? tasksDisabledMessage : undefined}
                  />
                ))}
              {tasksSubTab === 'activity' && <ActivityPanel />}
            </Box>
            {tasksSubTab === 'tasks' && <PropertyStatsFooter />}
          </Box>
        )}
      </Box>
    </>
  );
};

/* ─── Cascading avatars for everyone in the thread ────────── */

// Matches the Tooltip styling already used for the icon CTAs below.
const tooltipSlotProps = {
  tooltip: { sx: { mb: '0px !important' } },
  popper: { modifiers: [{ name: 'flip', options: { fallbackPlacements: [] } }] },
};

const ParticipantAvatars = ({ group }: { group: ClientGroup | undefined }) => {
  // Primary advisor + co-advisors are just user IDs on the group — resolve
  // their name/photo the same way ChatsPage already does for the buyer-side
  // advisor lookup (one profile query per ID, batched via useQueries).
  const advisorIds = useMemo(() => {
    if (!group) return [];
    return Array.from(new Set([group.advisorId, ...(group.coAdvisorIds ?? [])].filter(Boolean)));
  }, [group]);

  const advisorProfileResults = useQueries({
    queries: advisorIds.map((id) => ({
      queryKey: ['public-profile', id],
      queryFn: () => getPublicUserProfile(id),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const participants = useMemo(() => {
    if (!group) return [];
    const advisors = advisorIds.map((id, i) => ({
      key: id,
      name: advisorProfileResults[i]?.data?.name || 'Advisor',
      photoUrl: advisorProfileResults[i]?.data?.photoUrl,
    }));
    // Members (buyers/owners) already carry name/photoUrl directly — no fetch needed.
    const members = group.members.map((m) => ({
      key: m.userId ?? m.id,
      name: m.name,
      photoUrl: m.photoUrl,
    }));
    return [...advisors, ...members];
  }, [group, advisorIds, advisorProfileResults]);

  if (participants.length === 0) return null;

  const MAX_SHOWN = 4;
  const shown = participants.slice(0, MAX_SHOWN);
  const overflow = participants.length - shown.length;

  return (
    <Box sx={{ display: 'flex', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      {shown.map((p, i) => (
        <Tooltip key={p.key} title={p.name} placement="top" slotProps={tooltipSlotProps}>
          <Avatar
            src={p.photoUrl ?? undefined}
            alt={p.name}
            sx={{
              width: 26,
              height: 26,
              fontSize: 11,
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 600,
              border: '2px solid var(--tx-line)',
              bgcolor: 'rgb(68, 68, 68)',
              color: 'var(--tx-text)',
              ml: i === 0 ? 0 : '-8px',
            }}>
            {getInitials(p.name)}
          </Avatar>
        </Tooltip>
      ))}
      {overflow > 0 && (
        <Avatar
          sx={{
            width: 26,
            height: 26,
            fontSize: 10,
            fontFamily: 'Manrope, sans-serif',
            fontWeight: 600,
            ml: '-8px',
            border: '2px solid var(--tx-line)',
            bgcolor: 'var(--tx-fill-3)',
            color: 'var(--tx-text)',
          }}>
          +{overflow}
        </Avatar>
      )}
    </Box>
  );
};

/* ─── Advisor Header with photo + name + client switcher ── */

interface AdvisorHeaderProps {
  selectedGroupId: string | null;
  onSelectGroup: (id: string) => void;
  onOpenPropertyPicker?: () => void;
}

const AdvisorHeader = ({
  selectedGroupId,
  onSelectGroup,
  onOpenPropertyPicker,
}: AdvisorHeaderProps) => {
  const { isAdvisor } = useUserRole();
  const { groups } = useClientGroups();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Resolve active group: explicit selection > first available
  const group = groups.find((g) => g.id === selectedGroupId) ?? groups[0];

  const hasMultiple = groups.length > 1;

  // For buyers, fetch advisor's public profile (photo, real name, presence)
  const advisorUserId = !isAdvisor ? group?.advisorId : undefined;
  const { data: advisorProfile } = useQuery({
    queryKey: ['public-profile', advisorUserId],
    queryFn: () => getPublicUserProfile(advisorUserId as string),
    enabled: Boolean(advisorUserId),
    staleTime: 5 * 60 * 1000,
  });

  const clientUserId = isAdvisor ? group?.members[0]?.userId : undefined;
  const { data: clientPresence } = useQuery({
    queryKey: ['presence', clientUserId],
    queryFn: () => getUserPresence([clientUserId as string]),
    enabled: Boolean(clientUserId),
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  const getName = (g: typeof group) => {
    if (!g) return 'Advisor';
    if (isAdvisor) return g.members[0]?.name ?? 'Client';
    return advisorProfile?.name || 'Advisor';
  };

  const name = getName(group);
  const subtitle = isAdvisor ? 'Your Client' : 'Your Advisor';
  const photoUrl = isAdvisor ? group?.members[0]?.photoUrl : advisorProfile?.photoUrl;
  const clientLastActiveAt = clientUserId ? clientPresence?.[clientUserId] : undefined;
  const calLinkPath = !isAdvisor ? calLinkPathFrom(advisorProfile?.calLink) : null;

  useEffect(() => {
    if (!calLinkPath) return;
    (async () => {
      const cal = await getCalApi({ namespace: CAL_NAMESPACE });
      cal('ui', { hideEventTypeDetails: false, layout: 'month_view', theme: 'dark' });
    })();
  }, [calLinkPath]);

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={hasMultiple ? () => setMenuOpen(true) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          pl: '20px',
          pr: '20px',
          pb: '14px',
          mb: '4px',
          flexShrink: 0,
          cursor: hasMultiple ? 'pointer' : 'default',
          borderRadius: '8px',
          borderBottom: '1px solid var(--tx-line)',
        }}>
        <UserAvatar
          name={name}
          photoUrl={photoUrl}
          size={40}
          lastActiveAt={
            (isAdvisor ? clientLastActiveAt : advisorProfile?.lastActiveAt) ?? undefined
          }
          sx={{ bgcolor: '#555' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.3,
              }}>
              {name}
            </Typography>
            {hasMultiple && (
              <KeyboardArrowDown
                sx={{
                  fontSize: 18,
                  color: 'var(--tx-faint)',
                  transform: menuOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s ease',
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 12,
              lineHeight: 1.3,
            }}>
            {subtitle}
          </Typography>
        </Box>

        <ParticipantAvatars group={group} />

        {/* ─── Icon CTAs ────────────────────────────────────── */}
        {!isAdvisor && (
          <Box
            sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}>
            {calLinkPath && (
              <Tooltip
                title="Book a meeting"
                placement="top"
                slotProps={{
                  tooltip: { sx: { mb: '0px !important' } },
                  popper: { modifiers: [{ name: 'flip', options: { fallbackPlacements: [] } }] },
                }}>
                <IconButton
                  size="small"
                  onClick={async (e) => {
                    e.stopPropagation();
                    const cal = await getCalApi({ namespace: CAL_NAMESPACE });
                    cal('modal', { calLink: calLinkPath, config: { layout: 'month_view' } });
                  }}
                  sx={{
                    width: 30,
                    height: 30,
                    color: 'text.secondary',
                    border: 0,
                    backgroundColor: 'var(--tx-fill)',
                    borderRadius: '8px',
                    '&:hover': { color: 'text.primary', backgroundColor: 'var(--tx-fill-2)' },
                  }}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                </IconButton>
              </Tooltip>
            )}
            <Tooltip
              title="Submit a property"
              placement="top"
              slotProps={{
                tooltip: { sx: { mb: '0px !important' } },
                popper: { modifiers: [{ name: 'flip', options: { fallbackPlacements: [] } }] },
              }}>
              <IconButton
                size="small"
                onClick={() => onOpenPropertyPicker?.()}
                sx={{
                  width: 30,
                  height: 30,
                  color: 'text.secondary',
                  border: 0,
                  backgroundColor: 'var(--tx-fill)',
                  borderRadius: '8px',
                  '&:hover': { color: 'text.primary', backgroundColor: 'var(--tx-fill-2)' },
                }}>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {hasMultiple && (
        <Menu
          anchorEl={anchorRef.current}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                width: 260,
                maxHeight: 400,
                // a menu surface is the solid popover (Tradehaus .tx-pop), never the 5% tile tint that lets the page show through
                backgroundColor: 'var(--tx-dialog)',
                backgroundImage: 'var(--tx-surface-light)',
                boxShadow: 'var(--tx-shadow-sm)',
                borderRadius: '12px',
              },
            },
          }}
          sx={{
            '& .MuiMenuItem-root': {
              paddingTop: '6px !important',
              paddingBottom: '6px !important',
              paddingLeft: '16px !important',
              paddingRight: '16px !important',
              minHeight: '36px !important',
              borderBottomWidth: '0 !important',
              lineHeight: '1.4 !important',
              color: 'text.secondary',
              fontFamily: 'Manrope, sans-serif',
              fontSize: 14,
              borderRadius: '6px',
              mx: '6px',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
              '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.08)', color: '#ffffff' },
            },
            '& .MuiListItemIcon-root': {
              minWidth: 32,
              color: 'success2.main',
            },
          }}>
          {groups.map((g) => {
            const rName = getName(g);
            const isSelected = g.id === group?.id;
            return (
              <MenuItem
                key={g.id}
                onClick={() => {
                  onSelectGroup(g.id);
                  setMenuOpen(false);
                }}>
                <ListItemIcon>
                  {isSelected && <CheckIcon sx={{ fontSize: 18, color: 'success2.main' }} />}
                </ListItemIcon>
                <ListItemText
                  primary={rName}
                  secondary={subtitle}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontSize: '0.875rem',
                    fontFamily: 'Manrope, sans-serif',
                    color: 'var(--tx-text)',
                    fontWeight: 600,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    fontSize: '0.75rem',
                    fontFamily: 'Manrope, sans-serif',
                    color: 'text.secondary',
                  }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};

export default ChatPanel;
